/**
 * useHandsFreeScreen
 *
 * Thin hook a page uses to register its "voice screen" (what to read + which
 * commands are available) with the persistent HandsFreeProvider. The provider
 * (which lives above the router and survives navigation) does the actual
 * read-then-listen driving. When inactive, it registers null so the provider
 * stops reading and pauses the mic.
 */

import { useEffect, useRef } from 'react';
import type { ToolDefinition } from '@selna/session';
import { useHandsFree } from './HandsFreeProvider';

interface UseHandsFreeScreenArgs {
  /** enabled AND the screen is in an actionable state. */
  active: boolean;
  token: string;
  /** Identity of the read content — the provider re-reads when this changes. */
  readKey: string;
  readText: string;
  tools: ToolDefinition[];
  /**
   * If set and it matches the provider's current/last readKey, the provider
   * treats this as a continuation: it keeps that read going (or leaves it as
   * read) and speaks only `readTail` instead of restarting `readText`.
   */
  continuesReadKey?: string;
  /** Short text to append when `continuesReadKey` matches. */
  readTail?: string;
  onCommand: (name: string, args: Record<string, unknown>) => void;
}

export function useHandsFreeScreen(args: UseHandsFreeScreenArgs): void {
  const { registerScreen } = useHandsFree();

  // Keep latest values for the (stable) registered screen object.
  const tokenRef = useRef(args.token);
  tokenRef.current = args.token;
  const readTextRef = useRef(args.readText);
  readTextRef.current = args.readText;
  const toolsRef = useRef(args.tools);
  toolsRef.current = args.tools;
  const continuesReadKeyRef = useRef(args.continuesReadKey);
  continuesReadKeyRef.current = args.continuesReadKey;
  const readTailRef = useRef(args.readTail);
  readTailRef.current = args.readTail;
  const onCommandRef = useRef(args.onCommand);
  onCommandRef.current = args.onCommand;

  const active = args.active;
  const readKey = active ? args.readKey : null;

  useEffect(() => {
    if (!active || !readKey) {
      registerScreen(null);
      return;
    }
    registerScreen({
      token: tokenRef.current,
      readKey,
      readText: readTextRef.current,
      tools: toolsRef.current,
      continuesReadKey: continuesReadKeyRef.current,
      readTail: readTailRef.current,
      onCommand: (n, a) => onCommandRef.current(n, a),
    });
    return () => registerScreen(null);
  }, [active, readKey, registerScreen]);
}
